import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  Menu, 
  X, 
  BarChart2, 
  Users, 
  Wallet, 
  Shield, 
  Settings, 
  UserCog,
  LogOut,
  Home,
  ChevronRight,
  Sun,
  Moon,
  Bell,
  Search,
  Zap,
  ArrowUpCircle,
  ArrowDownCircle,
  Coins,
  FileText,
  Bitcoin,
  Check,
  Trash2,
  DollarSign 
} from 'lucide-react';
import { AdminAuthAPI, AdminTokenService, AdminNotificationsAPI } from '../../services/api';

const menuItems = [
  { text: 'Dashboard', icon: <BarChart2 className="w-5 h-5" />, path: '/dashboard' },
  { text: 'Users', icon: <Users className="w-5 h-5" />, path: '/users' },
  { 
    text: 'Wallet', 
    icon: <Wallet className="w-5 h-5" />, 
    path: '/wallet',
    children: [
      { text: 'All Wallets', icon: <Coins className="w-4 h-4" />, path: '/wallet' },
      { text: 'Deposits', icon: <ArrowDownCircle className="w-4 h-4" />, path: '/deposits' },
      { text: 'NowPayment Deposits', icon: <ArrowDownCircle className="w-4 h-4" />, path: '/admin/nowpayments' },
      { text: 'Withdrawals', icon: <ArrowUpCircle className="w-4 h-4" />, path: '/withdrawals' },
      { text: 'Wallet Settings', icon: <Settings className="w-4 h-4" />, path: '/wallet-settings' },
    ]
  },
  {
    text: 'Rewards',
    icon: <Coins className="w-5 h-5" />,
    label: 'Rewards',
    path: '/admin/rewards'
  },
  {
    text: 'BTC/USD',
    icon: <Bitcoin className="w-5 h-5" />,
    label: 'BTC Settings',
    path: '/btc-settings',
    permission: 'settings'
  },
  { 
    text: 'Bets', 
    icon: <DollarSign className="w-5 h-5" />, 
    path: '/bets' 
  },
  { text: 'KYC', icon: <Shield className="w-5 h-5" />, path: '/kyc' },
  { text: 'Admins', icon: <UserCog className="w-5 h-5" />, path: '/admins' },
  { text: 'Images', icon: <Settings className="w-5 h-5" />, path: '/home-images' },
  { text: 'Settings', icon: <Settings className="w-5 h-5" />, path: '/settings' },
];

function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState(['Wallet']);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  const navigate = useNavigate();
  const location = useLocation();
  const adminData = AdminTokenService.getAdminData();

  // Fetch notifications
  const fetchNotifications = useCallback(async (pageNum = 1, append = false) => {
    try {
      setLoading(true);
      const response = await AdminNotificationsAPI.getNotifications({
        page: pageNum,
        limit: 20
      });
      
      const newNotifications = response.data || [];
      if (append) {
        setNotifications(prev => [...prev, ...newNotifications]);
      } else {
        setNotifications(newNotifications);
      }
      
      setHasMore(newNotifications.length === 20);
      setPage(pageNum);
    } catch (error) {
      // console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await AdminNotificationsAPI.getUnreadCount();
      setUnreadCount(response.count || 0);
    } catch (error) {
      // console.error('Failed to fetch unread count:', error);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchNotifications(1, false);
    fetchUnreadCount();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
      // Optionally refresh notifications if dropdown is open
      if (notificationsOpen) {
        fetchNotifications(1, false);
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchUnreadCount, notificationsOpen]);

  // Mark notification as read
  const handleMarkAsRead = useCallback(async (notificationId) => {
    try {
      await AdminNotificationsAPI.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, read_status: 1 } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      // console.error('Failed to mark notification as read:', error);
    }
  }, []);

  // Mark all as read
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await AdminNotificationsAPI.markAllAsRead();
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => ({ ...n, read_status: 1 }))
      );
      setUnreadCount(0);
    } catch (error) {
      // console.error('Failed to mark all as read:', error);
    }
  }, []);

  // Delete notification
  const handleDeleteNotification = useCallback(async (notificationId) => {
    try {
      await AdminNotificationsAPI.deleteNotification(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.filter(n => n.id !== notificationId)
      );
      
      // Update unread count if needed
      const deleted = notifications.find(n => n.id === notificationId);
      if (deleted && !deleted.read_status) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      // console.error('Failed to delete notification:', error);
    }
  }, [notifications]);

  // Load more notifications
  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchNotifications(page + 1, true);
    }
  }, [loading, hasMore, page, fetchNotifications]);

  const handleLogout = () => {
    AdminAuthAPI.logout();
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const toggleMenu = (text) => {
    setExpandedMenus(prev => 
      prev.includes(text) 
        ? prev.filter(item => item !== text)
        : [...prev, text]
    );
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard Overview';
    if (path === '/users') return 'User Management';
    if (path === '/wallet') return 'Wallet Management';
    if (path === '/deposits') return 'Deposit Management';
    if (path === '/withdrawals') return 'Withdrawal Management';
    if (path === '/wallet-settings') return 'Wallet Settings';
    if (path === '/kyc') return 'KYC Verification';
    if (path === '/admins') return 'Admin Management';
    if (path === '/settings') return 'System Settings';
    return 'Dashboard';
  };

  // Get notification icon based on type
  const getNotificationIcon = (type, priority) => {
    const color = priority === 'high' ? 'text-red-500' : 
                  priority === 'medium' ? 'text-yellow-500' : 'text-blue-500';
    
    switch (type) {
      case 'deposit':
        return <ArrowDownCircle className={`w-5 h-5 ${color}`} />;
      case 'withdrawal':
        return <ArrowUpCircle className={`w-5 h-5 ${color}`} />;
      case 'kyc':
        return <Shield className={`w-5 h-5 ${color}`} />;
      case 'user':
        return <Users className={`w-5 h-5 ${color}`} />;
      case 'deposit_proof':
        return <FileText className={`w-5 h-5 ${color}`} />;
      case 'system':
      default:
        return <Zap className={`w-5 h-5 ${color}`} />;
    }
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flex h-screen bg-deep-black">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/80 md:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-72 transform md:relative md:translate-x-0 
        transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="flex flex-col h-full bg-gradient-to-b from-night-blue to-deep-black border-r border-gray-800">
          {/* Logo Section */}
          <div className="p-6 border-b border-gray-800">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl shadow-lg">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
                  Admin Panel
                </h1>
                <p className="text-xs text-charcoal-gray mt-1">Control Center</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = expandedMenus.includes(item.text);
              const isItemActive = isActive(item.path) || (hasChildren && item.children.some(child => isActive(child.path)));

              return (
                <div key={item.text} className="space-y-1">
                  <button
                    onClick={() => {
                      if (hasChildren) {
                        toggleMenu(item.text);
                      } else {
                        navigate(item.path);
                        setSidebarOpen(false);
                      }
                    }}
                    className={`
                      group flex items-center justify-between w-full px-4 py-3.5 rounded-xl
                      transition-all duration-300 hover:scale-[1.02] hover:shadow-lg
                      ${isItemActive
                        ? 'bg-gradient-to-r from-sky-500/20 to-blue-600/10 border-l-4 border-sky-500'
                        : 'hover:bg-gray-800/50 border-l-4 border-transparent'
                      }
                    `}
                  >
                    <div className="flex items-center">
                      <div className={`
                        p-2 rounded-lg mr-3 transition-colors
                        ${isItemActive
                          ? 'bg-gradient-to-br from-sky-500 to-blue-600 text-white'
                          : 'bg-gray-800 text-charcoal-gray group-hover:text-sky-400'
                        }
                      `}>
                        {item.icon}
                      </div>
                      <span className={`
                        font-medium transition-colors
                        ${isItemActive
                          ? 'text-white'
                          : 'text-gray-300 group-hover:text-white'
                        }
                      `}>
                        {item.text}
                      </span>
                    </div>
                    {hasChildren && (
                      <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${
                        isExpanded ? 'rotate-90' : ''
                      } text-charcoal-gray`} />
                    )}
                  </button>

                  {/* Sub-menu items */}
                  {hasChildren && isExpanded && (
                    <div className="ml-4 space-y-1 border-l-2 border-gray-800 pl-4">
                      {item.children.map((child) => (
                        <button
                          key={child.path}
                          onClick={() => {
                            navigate(child.path);
                            setSidebarOpen(false);
                          }}
                          className={`
                            group flex items-center w-full px-4 py-2.5 rounded-lg
                            transition-all duration-300 hover:scale-[1.02]
                            ${isActive(child.path)
                              ? 'bg-gradient-to-r from-sky-500/20 to-blue-600/10 text-sky-400'
                              : 'text-charcoal-gray hover:text-white hover:bg-gray-800/50'
                            }
                          `}
                        >
                          <div className="mr-3">
                            {child.icon}
                          </div>
                          <span className="text-sm">{child.text}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-gray-800">
            <div className="flex items-center space-x-3 p-3 rounded-xl bg-gray-900/50">
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold">
                    {adminData?.username?.charAt(0).toUpperCase() || 'A'}
                  </span>
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-night-blue" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {adminData?.username || 'Admin'}
                </p>
                <p className="text-xs text-charcoal-gray capitalize truncate">
                  {adminData?.role || 'Administrator'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-night-blue/80 backdrop-blur-sm border-b border-gray-800">
          <div className="px-6 h-16 flex items-center justify-between">
            {/* Left side */}
            <div className="flex items-center space-x-4">
              <button
                className="md:hidden p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              
              <div className="hidden md:block">
                <h1 className="text-xl font-bold text-white">{getPageTitle()}</h1>
                <div className="flex items-center space-x-2 text-sm text-charcoal-gray">
                  <Home className="w-3 h-3" />
                  <ChevronRight className="w-3 h-3" />
                  <span>{getPageTitle()}</span>
                </div>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="hidden md:block relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Search className="w-4 h-4 text-charcoal-gray" />
                </div>
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 bg-gray-900/50 border border-gray-800 rounded-xl text-sm text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-64"
                />
              </div>

              {/* Theme Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => {
                    setNotificationsOpen(!notificationsOpen);
                    if (!notificationsOpen) {
                      fetchNotifications(1, false);
                    }
                  }}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors relative"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-rose-500 text-xs text-white rounded-full flex items-center justify-center px-1.5">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {notificationsOpen && (
                  <div className="absolute right-0 mt-2 w-96 bg-night-blue border border-gray-800 rounded-xl shadow-2xl py-2 z-50 max-h-[500px] flex flex-col">
                    <div className="px-4 py-2 border-b border-gray-800 flex items-center justify-between">
                      <h3 className="font-bold text-white">Notifications</h3>
                      <div className="flex items-center space-x-2">
                        {unreadCount > 0 && (
                          <button
                            onClick={handleMarkAllAsRead}
                            className="text-xs text-sky-400 hover:text-sky-300 transition-colors"
                          >
                            Mark all read
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <Bell className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                          <p className="text-gray-400 text-sm">No notifications</p>
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`px-4 py-3 hover:bg-gray-800/50 transition-colors border-b border-gray-800/50 last:border-0 ${
                              !notification.read_status ? 'bg-sky-500/5' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  {getNotificationIcon(notification.type, notification.priority)}
                                  <p className="text-sm font-medium text-white">
                                    {notification.title}
                                  </p>
                                </div>
                                <p className="text-sm text-gray-300 mt-1">
                                  {notification.message}
                                </p>
                                <div className="flex items-center justify-between mt-2">
                                  <span className="text-xs text-charcoal-gray">
                                    {formatTime(notification.created_at)}
                                  </span>
                                  {notification.link && (
                                    <a 
                                      href={notification.link}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        navigate(notification.link);
                                        setNotificationsOpen(false);
                                      }}
                                      className="text-xs text-sky-400 hover:text-sky-300 transition-colors"
                                    >
                                      View details →
                                    </a>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-start space-x-2 ml-3">
                                {!notification.read_status && (
                                  <button
                                    onClick={() => handleMarkAsRead(notification.id)}
                                    className="text-gray-500 hover:text-sky-400 transition-colors"
                                    title="Mark as read"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteNotification(notification.id)}
                                  className="text-gray-500 hover:text-red-400 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            {!notification.read_status && (
                              <div className="mt-1">
                                <span className="text-xs text-sky-400">🔵 New</span>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                    
                    {notifications.length > 0 && hasMore && (
                      <div className="px-4 py-2 border-t border-gray-800">
                        <button
                          onClick={handleLoadMore}
                          disabled={loading}
                          className="text-sm text-sky-400 hover:text-sky-300 w-full text-center py-2 transition-colors disabled:opacity-50"
                        >
                          {loading ? 'Loading...' : 'Load more'}
                        </button>
                      </div>
                    )}
                    
                    <div className="px-4 py-2 border-t border-gray-800">
                      <button 
                        onClick={() => {
                          navigate('/notifications');
                          setNotificationsOpen(false);
                        }}
                        className="text-sm text-sky-400 hover:text-sky-300 w-full text-center py-2 transition-colors"
                      >
                        View all notifications
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-3 focus:outline-none group"
                >
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                    <span className="text-white font-bold text-sm">
                      {adminData?.username?.charAt(0).toUpperCase() || 'A'}
                    </span>
                  </div>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-night-blue border border-gray-800 rounded-xl shadow-2xl py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-800">
                      <p className="text-sm text-white font-medium">
                        {adminData?.username || 'Admin'}
                      </p>
                      <p className="text-xs text-charcoal-gray capitalize mt-1">
                        {adminData?.role || 'Administrator'}
                      </p>
                    </div>
                    
                    <div className="py-1">
                      <button
                        onClick={() => {
                          navigate('/settings');
                          setUserMenuOpen(false);
                        }}
                        className="w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-800/50 hover:text-white flex items-center transition-colors"
                      >
                        <Settings className="w-4 h-4 mr-3" />
                        Settings
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-2.5 text-sm text-rose-300 hover:bg-rose-500/10 hover:text-rose-200 flex items-center transition-colors"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Logout
                      </button>
                    </div>
                    
                    <div className="px-4 py-2 border-t border-gray-800">
                      <div className="flex items-center justify-between text-xs text-charcoal-gray">
                        <span>Last login:</span>
                        <span className="text-emerald-400">Today, 14:30</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="max-w-7xl mx-auto">
              <Outlet />
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-night-blue/50 border-t border-gray-800 py-4 px-6">
          <div className="flex items-center justify-between text-sm text-charcoal-gray">
            <div className="flex items-center space-x-4">
              <span>© {new Date().getFullYear()} Admin Panel</span>
              <span className="w-1 h-1 bg-charcoal-gray rounded-full" />
              <span>v2.0.0</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="flex items-center">
                <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse" />
                System Status: <span className="text-emerald-400 ml-1">Operational</span>
              </span>
              <span className="w-1 h-1 bg-charcoal-gray rounded-full" />
              <span>Last sync: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default Layout;